import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { InvoiceWasteSummary } from "../../../types/invoice-waste";
import { formatNumber, numberWithCommas } from "../../../utils";

interface PriceLadderTableProps {
  summary: InvoiceWasteSummary | null;
  loading: boolean;
}

/** A price that produced no movement reads as blank, exactly as the paper sheet does. */
const blankIfZero = (value: number, formatted: string) =>
  value === 0 ? "-" : formatted;

/**
 * สรุปยอดหักตามราคา — the Price / Quantity / Baht / Deduct block the branch has been
 * keeping by hand in Excel next to the route sheet.
 *
 * Rendered inside PriceLadderButton's modal, not on the page: it annotates the report
 * rather than being part of it, so it stays behind the icon next to the tab bar and
 * leaves the tables below at full width. The heading and its explanation live on the
 * dialog title; this file is only the ladder itself.
 *
 * Every price the van carries gets a row whether or not it moved, because the reader
 * checks the ladder against their own sheet line by line; dropping the settled prices
 * would make the two lists a different length and force them to re-find their place.
 *
 * A shortfall is NEGATIVE here (ขาด 3 ชิ้น reads -3) and a surplus positive — the
 * branch reads the ladder as stock against what was sold, so missing goes down. The
 * API's `shortage_qty` / `baht` count a shortfall UP, so both are negated for display
 * only; the money is the same calculation either way. A surplus is deliberately NOT
 * charged: the ยอดหัก column stays blank on those rows.
 *
 * ยอดหัก and ยอดหักรวม are shown NEGATIVE and red, matching the shortfall's -จำนวน /
 * -เป็นเงิน on the same row. The API sends them positive, so they are negated here;
 * a zero total stays 0.00 in the normal colour.
 */
export default function PriceLadderTable({
  summary,
  loading,
}: PriceLadderTableProps) {
  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={180} />
      </Box>
    );
  }

  // No ladder at all (a report with no waste checker, or nothing priced) — the report
  // above is still complete, so this annotation just stays away.
  if (!summary || summary.price_groups.length === 0) return null;

  return (
    <TableContainer sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>ราคา</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>
              จำนวน
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>
              เป็นเงิน
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>
              ยอดหัก
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {summary.price_groups.map((row) => (
            <TableRow key={row.price}>
              <TableCell>{numberWithCommas(row.price)}</TableCell>
              <TableCell align="right">
                {blankIfZero(row.shortage_qty, numberWithCommas(-row.shortage_qty))}
              </TableCell>
              <TableCell align="right">
                {blankIfZero(row.baht, formatNumber(-row.baht))}
              </TableCell>
              <TableCell align="right">
                {row.deduction_amount === 0 ? (
                  "-"
                ) : (
                  <Typography variant="body2" color="error" fontWeight={600}>
                    {formatNumber(-row.deduction_amount)}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow
            sx={{
              "& td": {
                borderTop: "2px solid",
                borderTopColor: "divider",
                borderBottom: "none",
                fontSize: "0.95rem",
              },
            }}
          >
            <TableCell colSpan={3} sx={{ fontWeight: 700 }}>
              ยอดหักรวม
            </TableCell>
            <TableCell align="right">
              <Typography
                component="span"
                color={summary.total_deduct === 0 ? "text.primary" : "error"}
                fontWeight={700}
              >
                {summary.total_deduct === 0
                  ? formatNumber(0)
                  : formatNumber(-summary.total_deduct)}
              </Typography>
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
